import { flyoutContainerRegistry } from "./FlyoutContainerRegistry";
import { atom } from "jotai";
import { useAtom } from "jotai";
import { useSearchParams } from "react-router-dom";
import { useEffect } from "react";

export interface FlyoutContainerState {
  key: string;
  flyoutParams: Record<string, string>;
}

// Create a simple atom for flyout state
const flyoutAtom = atom<FlyoutContainerState | null>(null);

// Create an atom for flyout close events
export const flyoutCloseEventAtom = atom<{
  key: string;
  timestamp: number;
} | null>(null);
export const useFlyoutContainer: () => {
  flyout: FlyoutContainerState | null;
  setFlyout: (flyout: FlyoutContainerState | null) => void;
  closeFlyout: () => void;
  navigateToFlyout: (
    key: string,
    flyoutParams?: Record<string, string>
  ) => void;
} = () => {
  const [flyout, setFlyout] = useAtom(flyoutAtom);
  const [, setFlyoutCloseEvent] = useAtom(flyoutCloseEventAtom);
  const [searchParams, setSearchParams] = useSearchParams();

  // Read flyout state from URL on mount
  useEffect(() => {
    const flyoutKey = searchParams.get("f_key");

    if (flyoutKey) {
      // Extract flyout parameters from URL
      const flyoutParams = Object.fromEntries(
        Array.from(searchParams.entries())
          .filter(([k]) => k.startsWith("f_") && k !== "f_key")
          .map(([k, v]) => [k.substring(2), v])
      );

      setFlyout({ key: flyoutKey, flyoutParams });
    }
  }, [searchParams, setFlyout]);

  const closeFlyout = () => {
    // Emit close event before clearing the flyout
    if (flyout) {
      setFlyoutCloseEvent({ key: flyout.key, timestamp: Date.now() });
    }

    setFlyout(null);
    // Remove flyout parameters from URL
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete("f_key");
    // Remove any f_* parameters
    Array.from(newSearchParams.keys())
      .filter((key) => key.startsWith("f_"))
      .forEach((key) => newSearchParams.delete(key));
    setSearchParams(newSearchParams);
  };

  const navigateToFlyout = (
    key: string,
    flyoutParams?: Record<string, string>
  ) => {
    const flyouts = flyoutContainerRegistry.getFlyouts();
    const entry = flyouts[key];
    const component = entry?.component;

    if (!component) {
      console.warn(
        `Flyout ${key} not found or no component registered in flyoutContainerRegistry.`
      );
      return;
    }

    setFlyout({ key, flyoutParams: flyoutParams || {} });

    // Update URL with normal search parameters
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("f_key", key);

    // Add flyout parameters (already strings)
    Object.entries(flyoutParams || {}).forEach(([k, v]) => {
      newSearchParams.set(`f_${k}`, v);
    });

    setSearchParams(newSearchParams);
  };

  return {
    flyout,
    setFlyout,
    closeFlyout,
    navigateToFlyout,
  };
};
